package com.solevo.dist.viewmodels

import androidx.compose.ui.text.AnnotatedString
import com.solevo.libraries.entities.country.CountryPhoneCallingCode
import com.solevo.libraries.entities.country.RemoteCountryService
import com.solevo.libraries.feature.authentication.login.LoginType
import com.solevo.libraries.feature.authentication.login.phoneNumber.LoginWithPhoneNumber
import com.solevo.libraries.feature.authentication.session.AuthenticationSession
import com.solevo.libraries.feature.authentication.session.components.startSession
import com.solevo.libraries.feature.authentication.session.data.accessToken
import com.solevo.libraries.feature.authentication.ui.login.phoneNumber.LoginPhoneNumberActions
import com.solevo.libraries.feature.authentication.ui.login.phoneNumber.LoginPhoneNumberEvents
import com.solevo.libraries.feature.authentication.ui.login.phoneNumber.LoginPhoneNumberScreenConfiguration
import com.solevo.libraries.feature.authentication.ui.login.phoneNumber.LoginPhoneNumberScreenErrors
import com.solevo.libraries.feature.authentication.ui.login.phoneNumber.LoginPhoneNumberState
import com.solevo.libraries.feature.authentication.ui.login.phoneNumber.LoginPhoneNumberViewModel
import com.solevo.libraries.feature.authentication.ui.login.phoneNumber.LoginPhoneNumberWithOTPIntegration
import com.solevo.libraries.ui.components.base.actions.DefaultActionEmitter
import com.solevo.libraries.ui.components.base.actions.HasActions
import com.solevo.libraries.ui.components.base.state.DefaultStateEmitter
import com.solevo.libraries.ui.components.base.state.HasState
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch

/**
 * Created by Ilija Vucetic on 19.9.25..
 * Copyright (c) 2025 Aktiia. All rights reserved.
 */
class StartViewModel(
    stateEmitter: HasState<LoginPhoneNumberState> = DefaultStateEmitter(
        LoginPhoneNumberState()
    ),
    actionsEmitter: HasActions<LoginPhoneNumberActions?> = DefaultActionEmitter(),
    override val loginWithPhoneNumber: LoginWithPhoneNumber,
    private val remoteCountryService: RemoteCountryService,
    private val authenticationSession: AuthenticationSession
) : LoginPhoneNumberWithOTPIntegration, HasState<LoginPhoneNumberState> by stateEmitter, HasActions<LoginPhoneNumberActions?> by actionsEmitter {

    private val _ui = MutableStateFlow(LoginUiState())
    val ui: StateFlow<LoginUiState> = _ui

    private val _effects = MutableSharedFlow<UiEffect>(extraBufferCapacity = 1)
    val effects: Flow<UiEffect> = _effects

    override val scope: CoroutineScope = MainScope()
    override val configuration: LoginPhoneNumberViewModel.Configuration =
        LoginPhoneNumberScreenConfiguration()

    init {
        checkOnce(
            authenticationSession = authenticationSession,
            scope = scope,
            navigateLogin = { println("👉 Ide na Login ekran") },
            navigateHome = { println("👉 Ide na Home ekran") }
        )
    }

    fun checkOnce(
        authenticationSession: AuthenticationSession,
        scope: CoroutineScope,
        navigateLogin: () -> Unit,
        navigateHome: () -> Unit
    ) {
        scope.launch {
            delay(1000)
            val auth: FirebaseAuth = Firebase.auth
            val user = auth.currentUser

            if (user != null) {
                println("✅ Ulogovan, uid=${Firebase.auth.currentUser?.uid}")
            } else {
                println("❌ Nije ulogovan")
            }

            println("Token je: " + authenticationSession.authenticationState.value.accessToken()?.token)
            val signedInNow =
                authenticationSession.authenticationState.value.accessToken()?.token?.isNotEmpty()
                    ?: "".isNotEmpty()
            if (signedInNow) navigateHome() else navigateLogin()
        }
    }

    override suspend fun handleEvent(event: LoginPhoneNumberEvents) {
        super.handleEvent(event)

        when (event) {
            is LoginPhoneNumberEvents.PhoneNumberInputChanged -> {
                _ui.value = _ui.value.copy(phoneNumberError = null)
            }
        }
        println("event: $event")
    }

    override suspend fun handleContinueAction() {
        super.handleContinueAction()
        println("event: handleContinueAction")
    }

    override suspend fun handleLoginWithPhoneNumberSuccess(loginType: LoginType.PhoneNumber) {
        super.handleLoginWithPhoneNumberSuccess(loginType)
        println("✅ Phone number login success")
        handleUiEvents(LoginUiEvents.OtpStepUpdate)
        updateState { state ->
            state.copy(isLoadingInProgress = false)
        }
    }

    override suspend fun handleError(loginType: LoginType.PhoneNumber, error: LoginPhoneNumberScreenErrors): Boolean {
        println("❌ Phone number login error: ${error.cause}")
        // TEMPORARY DIAGNOSTIC: the UI shows the same "Please check your phone number" for every
        // failure, so surface what actually went wrong. Remove once the staging issue is understood.
        (error.cause as? com.solevo.libraries.network.infrastructure.NetworkHTTPFailure)?.let {
            println("❌ PHONE http failure: status=${it.body.status} code=${it.body.errorCode} msg=${it.body.errorMessage}")
        }
        (error.cause as? com.solevo.libraries.network.infrastructure.NetworkHTTPFailureBodySerializationError)?.let {
            println("❌ PHONE raw server body >>>${it.json}<<<")
        }
        val causeChain = generateSequence(error.cause) { it.cause }
            .joinToString(separator = " <- ") { "${it::class.simpleName}: ${it.message}" }
        println("❌ PHONE cause chain: $causeChain")
        println("event: handleLoginWithPhoneNumberError")
        _ui.value = _ui.value.copy(phoneNumberError = "Please check your phone number")
        updateState { state ->
            state.copy(isLoadingInProgress = false)
        }
        return true
    }

    fun handleUiEvents(event: LoginUiEvents) {
        when (event) {
            is LoginUiEvents.OtpStepUpdate -> {
                _ui.value = _ui.value.copy(step = Step.OTP, otpResent = false)
                startResendCooldown()
            }
            is LoginUiEvents.MethodStepUpdate -> {
                resendCooldownJob?.cancel()
                _ui.value = _ui.value.copy(step = Step.METHOD, resendCooldownSeconds = 0, otpResent = false)
            }
            is LoginUiEvents.ModePhoneUpdate -> _ui.value = _ui.value.copy(mode = Mode.PHONE)
            is LoginUiEvents.ModeEmailUpdate -> _ui.value = _ui.value.copy(mode = Mode.EMAIL)
            is LoginUiEvents.OTPUpdate -> _ui.value = _ui.value.copy(otp = event.value, otpResent = false)
            is LoginUiEvents.OTPResend -> {
                scope.launch { resendOtp() }
            }
            is LoginUiEvents.OTPSubmit -> {
                scope.launch { submitOtp() }
            }
            is LoginUiEvents.ClearOtpError -> {
                _ui.value = _ui.value.copy(otpError = null)
            }
        }
    }

    /** Load countries and preselect based on user's IP (fallback to first). */
    fun loadCountries() {
        scope.launch {
            val countriesResult = remoteCountryService.fetchCountriesPhoneCallingCodes()
            val ipResult = remoteCountryService.fetchCountryByIpAddress()

            val countries = countriesResult.getOrNull().orEmpty()
            val ipCode = ipResult.getOrNull()?.countryCode

            val selected = if (countries.isNotEmpty()) {
                countries.firstOrNull {
                    it.isoCode.twoCharacters.equals(ipCode, ignoreCase = true)
                } ?: countries.first()
            } else null
            selected?.let { selectCountry(it) }

            _ui.value = _ui.value.copy(
                countries = countries,
                selectedCountry = selected
            )
        }
    }

    private fun RemoteCountryService.CountryPayload.PhoneCallingCode.toDomain(): CountryPhoneCallingCode =
        CountryPhoneCallingCode(
            name = name,
            isoCode = isoCode,
            code = callingCode,
            regexPattern = regexPattern
        )

    fun selectCountry(country: RemoteCountryService.CountryPayload.PhoneCallingCode) {
        scope.launch {
            _ui.value = _ui.value.copy(selectedCountry = country)
            handleEvent(
                LoginPhoneNumberEvents.PhoneNumberCountryChanged(country.toDomain())
            )
        }
    }


    private var resendCooldownJob: Job? = null

    /**
     * Same 60 second cooldown as the mobile OTP screen: it runs after the first code is sent and
     * after every resend, so the button cannot be used to fire SMS requests back to back.
     */
    private fun startResendCooldown(seconds: Int = ResendCooldownSeconds) {
        resendCooldownJob?.cancel()
        _ui.value = _ui.value.copy(resendCooldownSeconds = seconds)
        resendCooldownJob = scope.launch {
            var left = seconds
            while (left > 0) {
                delay(1000)
                left -= 1
                _ui.value = _ui.value.copy(resendCooldownSeconds = left)
            }
        }
    }

    // Re-sends the SMS for the number already entered (LoginWithPhoneNumberAndOTP.resendCode); the
    // similarly named LoginWithPhoneNumber.resendCode is the custom-token fallback, not an SMS.
    private suspend fun resendOtp() {
        val current = ui.value
        if (current.isResending || current.resendCooldownSeconds > 0) return
        _ui.value = current.copy(isResending = true, otpError = null, otpResent = false)

        val result = loginWithPhoneNumber.withOTP.resendCode()

        result.fold(
            onSuccess = {
                _ui.value = _ui.value.copy(isResending = false, otp = "", otpResent = true)
                startResendCooldown()
            },
            onFailure = { err ->
                println("❌ OTP resend error: $err")
                _ui.value = _ui.value.copy(
                    isResending = false,
                    otpError = err.message ?: "Could not resend the code. Please try again."
                )
            }
        )
    }

    private suspend fun submitOtp() {
        val code = ui.value.otp.trim()
        _ui.value = _ui.value.copy(otpError = null)
        updateState { it.copy(isLoadingInProgress = true) }

        val result = loginWithPhoneNumber.verifyOTP(code)

        updateState { it.copy(isLoadingInProgress = false) }

        result.fold(
            onSuccess = {
                _ui.value = _ui.value.copy(otpError = null)
                _effects.emit(UiEffect.NavigateToNextStep)
                println("OTP is" + loginWithPhoneNumber.withOTP.parameters.otp)
                authenticationSession.startSession()
            },
            onFailure = { err ->
                println("❌ OTP error: $err")
                val errorMessage = err.message ?: "Verification failed. Please try again."
                _ui.value = _ui.value.copy(otpError = errorMessage)
                /*updateState {
                    it.copy(
                        phoneNumberValidationMessage = { AnnotatedString("Invalid phone number") },
                        errorMessage = { AnnotatedString(errorMessage) }
                    )
                }*/
            }
        )
    }
}


data class LoginUiState(
    val step: Step = Step.METHOD,
    val mode: Mode = Mode.PHONE,
    val otp: String = "",
    val countries: List<RemoteCountryService.CountryPayload.PhoneCallingCode> = emptyList(),
    val selectedCountry: RemoteCountryService.CountryPayload.PhoneCallingCode? = null,
    val phoneNumberError: String? = null,
    val otpError: String? = null,
    val isResending: Boolean = false,
    val resendCooldownSeconds: Int = 0,
    val otpResent: Boolean = false,
)

interface LoginUiEvents {
    data object OtpStepUpdate : LoginUiEvents
    data object MethodStepUpdate : LoginUiEvents
    data object ModePhoneUpdate : LoginUiEvents
    data object ModeEmailUpdate : LoginUiEvents
    data class OTPUpdate(val value: String) : LoginUiEvents
    data object OTPSubmit : LoginUiEvents
    data object OTPResend : LoginUiEvents
    data object ClearOtpError : LoginUiEvents
}

sealed interface UiEffect {
    data class ShowError(val message: String) : UiEffect
    data object NavigateToNextStep : UiEffect
}

//region ENUMS
private const val ResendCooldownSeconds = 60

enum class Step { METHOD, OTP }
enum class Mode { PHONE, EMAIL }
//endregion